import * as React from 'react'

import type { AppointmentStatusBreakdown } from '@sdr-agend/admin-schemas'

/**
 * "Status dos Agendamentos" (Story 10.7, 2026-07-30, user request, AC3) —
 * donut de 3 fatias (Confirmados/Reagendados/Cancelados), plain SVG (sem lib
 * de gráfico — mesma convenção de `AttendanceChart.tsx`). Cada fatia usa uma
 * cor semântica de status já estabelecida no dashboard (emerald=bom,
 * amber=atenção, rose=ruim — mesmas cores de `KpiCard`'s `status`/`accent`),
 * com legenda por texto (nunca só cor, ver skill `dataviz`).
 */
export interface AppointmentStatusDonutProps {
  data: AppointmentStatusBreakdown
}

interface Slice {
  key: string
  label: string
  value: number
  colorClass: string
  strokeHex: string
}

const RADIUS = 40
const STROKE_WIDTH = 14
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

function buildSlices(data: AppointmentStatusBreakdown): Slice[] {
  return [
    { key: 'confirmed', label: 'Confirmados', value: data.confirmed, colorClass: 'bg-emerald-500', strokeHex: '#10b981' },
    { key: 'rescheduled', label: 'Reagendados', value: data.rescheduled, colorClass: 'bg-amber-500', strokeHex: '#f59e0b' },
    { key: 'cancelled', label: 'Cancelados', value: data.cancelled, colorClass: 'bg-rose-500', strokeHex: '#f43f5e' },
  ]
}

export function AppointmentStatusDonut({ data }: AppointmentStatusDonutProps): React.ReactElement {
  const slices = buildSlices(data)
  const total = slices.reduce((sum, s) => sum + s.value, 0)

  if (total === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="appointment-status-donut-empty"
      >
        Nenhum agendamento confirmado, reagendado ou cancelado no período selecionado.
      </div>
    )
  }

  let cumulative = 0

  return (
    <div
      className="flex flex-col items-center gap-4 sm:flex-row sm:items-center"
      data-testid="appointment-status-donut"
    >
      <svg
        width={2 * (RADIUS + STROKE_WIDTH / 2)}
        height={2 * (RADIUS + STROKE_WIDTH / 2)}
        viewBox={`0 0 ${2 * (RADIUS + STROKE_WIDTH / 2)} ${2 * (RADIUS + STROKE_WIDTH / 2)}`}
        role="img"
        aria-label={`Status dos agendamentos: ${slices.map((s) => `${s.label} ${s.value}`).join(', ')}`}
      >
        <g transform={`translate(${RADIUS + STROKE_WIDTH / 2}, ${RADIUS + STROKE_WIDTH / 2}) rotate(-90)`}>
          {slices
            .filter((s) => s.value > 0)
            .map((s) => {
              const fraction = s.value / total
              const dashArray = `${fraction * CIRCUMFERENCE} ${CIRCUMFERENCE}`
              const dashOffset = -cumulative * CIRCUMFERENCE
              cumulative += fraction
              return (
                <circle
                  key={s.key}
                  r={RADIUS}
                  fill="none"
                  stroke={s.strokeHex}
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                  data-testid={`appointment-status-slice-${s.key}`}
                />
              )
            })}
        </g>
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-foreground text-lg font-semibold tabular-nums"
        >
          {total.toLocaleString('pt-BR')}
        </text>
      </svg>

      <div className="grid gap-1.5">
        {slices.map((s) => (
          <div key={s.key} className="flex items-center gap-2 text-xs" data-testid={`appointment-status-legend-${s.key}`}>
            <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${s.colorClass}`} />
            <span className="text-muted-foreground">{s.label}</span>
            <span className="font-medium tabular-nums">{s.value.toLocaleString('pt-BR')}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AppointmentStatusDonut
